import {
  handleGetRentalCarouselAction,
  handleGetRentalDetailAction,
  handleGetRentalFilterCriteria,
  GetRentalListingParams,
  handleGetRentalListing,
} from "@/actions/rental.action";
import {
  RentalCarouselData,
  RentalListingItem,
  RentalListingRes,
} from "@/components/main-services/rentals/rental.type";
import { ApiResult, RentalFilterCriteriaResponse } from "@/types/api";
import {
  RentalDetailParams,
  RentalDetailResponse,
} from "@/types/rental-detail";
import { useQuery, UseQueryResult } from "@tanstack/react-query";

const rentalQueryKeys = {
  carousel: () => ["rental-carousel"] as const,
  detail: (rentalId: string, params: RentalDetailParams) =>
    ["rental-detail", rentalId, params] as const,
  criteria: () => ["rental-criteria"] as const,
  listing: (params: GetRentalListingParams) =>
    ["rental-listing", params] as const,
};

/**
 * Example: invalidate called:
 * queryClient.invalidateQueries({ queryKey: rentalQueryKeys.carousel() });
 */

const useRentalsCarousel = (enabled = true) => {
  return useQuery<ApiResult<RentalCarouselData>, Error, RentalCarouselData>({
    queryKey: rentalQueryKeys.carousel(),
    queryFn: () => handleGetRentalCarouselAction(),
    enabled,
    staleTime: 1000 * 60 * 5,
    select: (res) => res.data,
  });
};

const useRentalDetail = (rentalId: string, params: RentalDetailParams) => {
  return useQuery<RentalDetailResponse>({
    queryKey: rentalQueryKeys.detail(rentalId, params),
    queryFn: () => handleGetRentalDetailAction(rentalId, params),
    enabled: Boolean(rentalId),
    staleTime: 5 * 60 * 1000,
    placeholderData: (previous) => previous,
  });
};

const useRentalFilterCriteria = (enabled = true) => {
  return useQuery<RentalFilterCriteriaResponse>({
    queryKey: rentalQueryKeys.criteria(),
    queryFn: () => handleGetRentalFilterCriteria(),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
};

const useGetRentalListing = (
  params: GetRentalListingParams,
  enabled = true,
): UseQueryResult<RentalListingItem[], Error> => {
  return useQuery({
    queryKey: rentalQueryKeys.listing(params),
    queryFn: () => handleGetRentalListing(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
    enabled,
    select: (res: ApiResult<RentalListingRes>) => res.data.data,
  });
};

export {
  rentalQueryKeys,
  useRentalsCarousel,
  useRentalDetail,
  useRentalFilterCriteria,
  useGetRentalListing,
};
